import FirmwareIcon from '@mui/icons-material/Memory';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { clampPercent } from '../../../utils/programDeployProgressUtils';
import HelpPopover from '../../HelpPopover';

interface FirmwareStatusPanelProps {
  isPreparingTransfer: boolean;
  isTransferring: boolean;
  isFinalizing: boolean;
  needsDownload: boolean;
  needsInstall: boolean;
  assetId: string | undefined;
  currentAssetState: string | undefined;
  percent: number;
  downloadDisabled?: boolean;
  installDisabled?: boolean;
  onDownload: () => void;
  onInstall: () => void;
}

const FirmwareStatusPanel = ({
  isPreparingTransfer,
  isTransferring,
  isFinalizing,
  needsDownload,
  needsInstall,
  assetId,
  currentAssetState,
  percent,
  downloadDisabled = false,
  installDisabled = false,
  onDownload,
  onInstall,
}: FirmwareStatusPanelProps) => {
  const { t } = useTranslation();
  const isTransferComplete = currentAssetState === 'idle';
  const displayPercent = isTransferComplete ? 100 : clampPercent(percent);
  const assetLabel = assetId ?? t('connectedDrawer.firmware-transfer-asset-fallback', 'Firmware asset');
  const currentAssetStateLabel =
    currentAssetState === 'checking'
      ? t('connectedDrawer.firmware-transfer-state.checking', 'Checking')
      : currentAssetState === 'downloading'
      ? t('connectedDrawer.firmware-transfer-state.downloading', 'Downloading')
      : currentAssetState === 'sending'
      ? t('connectedDrawer.firmware-transfer-state.sending', 'Transferring')
      : currentAssetState === 'idle'
      ? t('connectedDrawer.firmware-transfer-state.idle', 'Complete')
      : undefined;
  const assetStateLabel = currentAssetStateLabel
    ? t('connectedDrawer.firmware-transfer-asset-state', {
        asset: assetLabel,
        state: currentAssetStateLabel,
        defaultValue: '{{asset}} ({{state}})',
      })
    : assetLabel;

  if (isPreparingTransfer) {
    return (
      <Stack direction={'row'} spacing={0.5} alignItems="center" justifyContent={'center'} pb={1}>
        <Typography>
          <Trans i18nKey="connectedDrawer.firmware-update-available">New firmware is available</Trans>
        </Typography>
        <HelpPopover
          id="firmware-update-available-popover"
          ariaLabel={t('aria-label.more-info', 'more information')}
          text={<Trans i18nKey="connectedDrawer.firmware-update-available-info">New firmware is available information</Trans>}
        />
      </Stack>
    );
  }

  if (isTransferring) {
    return (
      <>
        <Stack direction={'row'} spacing={0.5} alignItems="center" justifyContent={'center'} pb={1}>
          <Typography>
            <Trans i18nKey="connectedDrawer.firmware-transferring">Downloading and transferring firmware</Trans>
          </Typography>
          <HelpPopover
            id="firmware-transferring-popover"
            ariaLabel={t('aria-label.more-info', 'more information')}
            text={<Trans i18nKey="connectedDrawer.firmware-transferring-info">Downloading and transferring firmware information</Trans>}
          />
        </Stack>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {assetStateLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${displayPercent.toFixed(1)}%`}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={displayPercent} sx={{ height: 4, borderRadius: 2 }} />
        </Box>
      </>
    );
  }

  if (isFinalizing) {
    return (
      <>
        <Stack direction={'row'} spacing={0.5} alignItems="center" justifyContent={'center'} pb={1}>
          <Typography>
            <Trans i18nKey="connectedDrawer.firmware-finalizing">Applying firmware updates</Trans>
          </Typography>
          <HelpPopover
            id="firmware-finalizing-popover"
            ariaLabel={t('aria-label.more-info', 'more information')}
            text={<Trans i18nKey="connectedDrawer.firmware-finalizing-info">Applying firmware updates information</Trans>}
          />
        </Stack>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              <Trans i18nKey="connectedDrawer.firmware-finalizing-progress">Finalizing firmware update</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${clampPercent(percent).toFixed(1)}%`}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={clampPercent(percent)} sx={{ height: 4, borderRadius: 2 }} />
        </Box>
      </>
    );
  }

  if (needsDownload) {
    return (
      <>
        <Stack direction={'row'} spacing={0.5} alignItems="center" justifyContent={'center'} pb={1}>
          <Typography>
            <Trans i18nKey="connectedDrawer.firmware-update-available">New firmware is available</Trans>
          </Typography>
          <HelpPopover
            id="firmware-download-available-popover"
            ariaLabel={t('aria-label.more-info', 'more information')}
            text={<Trans i18nKey="connectedDrawer.firmware-update-available-info">New firmware is available information</Trans>}
          />
        </Stack>
        <Stack direction={'row'} spacing={0.5} alignItems="center">
          <FirmwareIcon fontSize="large" />
          <Button onClick={onDownload} disabled={downloadDisabled} sx={{ border: 1, borderColor: 'primary.main', borderRadius: 6 }}>
            <Trans i18nKey="connectedDrawer.button.download-firmware">Download</Trans>
          </Button>
        </Stack>
      </>
    );
  }

  if (needsInstall) {
    return (
      <>
        <Stack direction={'row'} spacing={0.5} alignItems="center" justifyContent={'center'} pb={1}>
          <Typography>
            <Trans i18nKey="connectedDrawer.firmware-ready-for-install">Firmware is ready for installation</Trans>
          </Typography>
          <HelpPopover
            id="firmware-ready-for-install-popover"
            ariaLabel={t('aria-label.more-info', 'more information')}
            text={
              <Trans i18nKey="connectedDrawer.firmware-ready-for-installation-info">
                Firmware is ready for installation. Click the button to install the firmware.
              </Trans>
            }
          />
        </Stack>
        <Stack direction={'row'} spacing={0.5} alignItems="center">
          <FirmwareIcon fontSize="large" />
          <Button onClick={onInstall} disabled={installDisabled} sx={{ border: 1, borderColor: 'primary.main', borderRadius: 6 }}>
            <Trans i18nKey="connectedDrawer.button.install-firmware">Install Firmware</Trans>
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <Stack direction={'row'} spacing={0.5} alignItems="center" justifyContent={'center'}>
      <Typography fontSize={15}>
        <Trans i18nKey="connectedDrawer.firmware-up-to-date">Firmware is up to date</Trans>
      </Typography>
      <HelpPopover
        id="firmware-up-to-date-popover"
        ariaLabel={t('aria-label.more-info', 'more information')}
        text={<Trans i18nKey="connectedDrawer.firmware-up-to-date-info">Firmware is up to date information</Trans>}
      />
    </Stack>
  );
};

export default FirmwareStatusPanel;
